'use client';

import { ReactNode } from 'react';

import { useTranslation } from '@/lib/i18n/context';

export interface CaseStudyAsideExtra {
  label: ReactNode;
  value: ReactNode;
}

export interface CaseStudyAsideProps {
  logoSrc?: string;
  logoAlt: string;
  /** Fallback customer name shown if no logo is provided */
  customerName?: string;
  company: ReactNode;
  industry: ReactNode;
  websiteUrl: string;
  websiteLabel?: string;
  keyResults: string[];
  /** Optional extra rows shown between Industry and Website (e.g. "Company size") */
  extras?: CaseStudyAsideExtra[];
}

export const CaseStudyAside = ({
  logoSrc,
  logoAlt,
  customerName,
  company,
  industry,
  websiteUrl,
  websiteLabel,
  keyResults,
  extras = [],
}: CaseStudyAsideProps) => {
  const { t } = useTranslation();
  const cleanedWebsiteLabel =
    websiteLabel ??
    websiteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');

  return (
    <aside className="lg:w-[280px] lg:shrink-0">
      <div className="rounded-2xl border border-neutral-200 bg-[#fafaf9] p-6 lg:sticky lg:top-28">
        {logoSrc ? (
          <img
            src={logoSrc}
            alt={logoAlt}
            className="mb-6 max-h-8 w-auto opacity-90"
          />
        ) : (
          <div className="mb-6 text-2xl font-medium tracking-tight text-neutral-900">
            {customerName ?? logoAlt}
          </div>
        )}
        <dl className="space-y-5">
          <div>
            <dt className="text-[11px] font-medium tracking-wider uppercase text-neutral-500">
              {t.customerStories.Company}
            </dt>
            <dd className="mt-1 text-sm text-neutral-700">{company}</dd>
          </div>
          <div>
            <dt className="text-[11px] font-medium tracking-wider uppercase text-neutral-500">
              {t.customerStories.Industry}
            </dt>
            <dd className="mt-1 text-sm text-neutral-700">{industry}</dd>
          </div>
          {extras.map((extra, i) => (
            <div key={i}>
              <dt className="text-[11px] font-medium tracking-wider uppercase text-neutral-500">
                {extra.label}
              </dt>
              <dd className="mt-1 text-sm text-neutral-700">{extra.value}</dd>
            </div>
          ))}
          <div>
            <dt className="text-[11px] font-medium tracking-wider uppercase text-neutral-500">
              {t.customerStories.Website}
            </dt>
            <dd className="mt-1 text-sm">
              <a
                href={websiteUrl}
                target="_blank"
                rel="noreferrer"
                className="text-neutral-700 underline underline-offset-4 hover:text-neutral-900"
              >
                {cleanedWebsiteLabel}
              </a>
            </dd>
          </div>
          {keyResults.length > 0 && (
            <div>
              <dt className="text-[11px] font-medium tracking-wider uppercase text-neutral-500">
                {t.customerStories['Key Results']}
              </dt>
              <dd className="mt-2 space-y-1.5 text-sm text-neutral-700">
                {keyResults.map((line, i) => (
                  <div key={i}>{line}</div>
                ))}
              </dd>
            </div>
          )}
        </dl>
      </div>
    </aside>
  );
};

export default CaseStudyAside;
